/**
* All rights Reserved, Designed By www.feidai.com
* @Title: jielong.ts
* @Package:
* @author: 青瞳科技
* @date: 2026/06/26 11:47:00
* @version: V1.0
* @Copyright: 2019 www.feidai.com Inc. All rights reserved.
* @Belong To: 青瞳科技
* 注意：本内容仅限于深圳青瞳科技有限公司内部传阅，除非适用法律要求或书面同意，禁止外泄以及用于其他的商业目的
*/

// 接龙介绍内容编辑器 - 共享类型与接口
import { api, axios, axiosAPIUrl, Parameter, assignParams, exportAxios } from "./index";
import xAxios from "axios";

export type JielongIntroCardType =
  | "bigImage"
  | "smallImage"
  | "video"
  | "text"
  | "tag"
  | "aiText";

export interface JielongIntroCardDetail {
  sortNo: number;
  type: JielongIntroCardType;
  value: string | string[] | null;
}

/** 提交给后端的介绍卡片：string[]（小图/标签等）需 JSON 序列化为一层字符串 */
export interface JielongIntroCardDetailApi {
  sortNo: number;
  type: JielongIntroCardType;
  value: string | null;
}

/** 提交前：数组 value 转 JSON 字符串，其余保持原样 */
export function serializeDetailListForApi (
  list: JielongIntroCardDetail[]
): JielongIntroCardDetailApi[] {
  return (list || []).map((item) => ({
    sortNo: item.sortNo,
    type: item.type,
    value: Array.isArray(item.value) ? JSON.stringify(item.value) : (item.value ?? null),
  }));
}

/** 详情回填：把后端 JSON 字符串还原为 string[]（兼容已是数组的情况） */
export function parseDetailListFromApi (
  list: Array<JielongIntroCardDetail | JielongIntroCardDetailApi> | null | undefined
): JielongIntroCardDetail[] {
  return (list || []).filter(Boolean).map((item) => {
    const { value } = item;
    if (Array.isArray(value)) {
      return { sortNo: item.sortNo, type: item.type, value };
    }
    if (typeof value === "string" && (item.type === "smallImage" || item.type === "tag")) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return { sortNo: item.sortNo, type: item.type, value: parsed };
        }
      } catch (_) {
        // 非 JSON 时保持原字符串
      }
    }
    return { sortNo: item.sortNo, type: item.type, value: value ?? null };
  });
}

/** 常用标签（前端展示用，由 getTagConfigList 映射） */
export interface CommonTag {
  tagId: string;
  label: string;
  custom?: boolean; // !isDefault，用户自定义可删除
}

/** 后端标签配置项 */
export interface GoodsTagConfigItem {
  tagId: string;
  tagName: string;
  isDefault: boolean | number;
}

/** 将后端标签配置映射为 CommonTag；isDefault 兼容 1/0、true/false、"1"/"0" */
export function mapTagConfigToCommonTag (item: GoodsTagConfigItem): CommonTag {
  const raw = item.isDefault as boolean | number | string;
  const isDefault = raw === true || raw === 1 || raw === "1";
  return {
    tagId: item.tagId,
    label: item.tagName,
    custom: !isDefault,
  };
}

// ===== AI 生成接龙介绍（常小二：一次请求返回完整结果，非流式/非轮询） =====

/** AI 入参商品：仅五字段，与前端商品表单对应 */
export interface AiGenerateProduct {
  saleTitle: string;
  tags: string[] | null;
  specification: string;
  price: number;
  originalPrice: number | null;
}

export interface AiGenerateCopyOptions {
  length?: "short" | "medium" | "long";
  tone?: string;
  emoji?: string;
  /** 重新生成时递增，帮助模型输出不同版本 */
  variant?: number;
}

export interface AiGenerateParams {
  /** 同一次生成的网络重试保持不变 */
  requestId: string;
  source: "nanguatou";
  products: AiGenerateProduct[];
  copyOptions?: AiGenerateCopyOptions;
}

export interface AiGenerateResult {
  requestId: string;
  cards: JielongIntroCardDetail[];
  warnings?: string[];
  draft?: boolean;
  finishReason?: string;
  traceId?: string;
}

/**
 * 解析常小二请求地址。
 * axiosAPIUrl 的 baseURL 为 /api，相对路径会被错误拼接，故相对地址补全为当前 origin。
 */
function resolveChangxiaoerUrl (path: string): string {
  const base = (process.env.VUE_APP_AI_URL || "").replace(/\/?$/, "/");
  const url = `${base}${path.replace(/^\//, "")}`;
  if (/^https?:\/\//i.test(url)) return url;
  const normalized = url.startsWith("/") ? url : `/${url}`;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${normalized}`;
  }
  return normalized;
}

// export function aiGenerateIntro(params: AiGenerateParams): Promise<AiGenerateResult> {
//   return axiosAPIUrl({
//     url: "/changxiaoer/api/ai/solitaire-copy/generate",
//     method: "post",
//     data: params,
//   });
// }

/**
 * AI 生成接龙介绍内容（单次 POST，整包返回 cards）
 * @param params 请求体，见常小二 /api/ai/solitaire-copy/generate
 * @param signal 用于取消进行中的请求（经 extraParams.abortSignal 接入，避免被 cancelRepeatRequest 覆盖）
 */
export async function aiGenerateIntro (
  params: AiGenerateParams,
  signal?: AbortSignal
): Promise<AiGenerateResult> {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }

  try {
    const body: any = await axiosAPIUrl({
      url: "/changxiaoer/api/ai/solitaire-copy/generate",
      method: "post",
      data: params,
      timeout: 20000,
      extraParams: {
        showLoading: false,
        showToastTip: false,
        abortSignal: signal,
      },
    });

    // 兼容常小二直出 与 网关包装 { code, data }
    const payload: AiGenerateResult | undefined =
      body && Array.isArray(body.cards)
        ? body
        : body?.data && Array.isArray(body.data.cards)
          ? body.data
          : undefined;

    if (!payload) {
      const msg =
        body?.msg ||
        body?.message ||
        body?.error ||
        "生成失败，请稍后重试";
      throw new Error(msg);
    }
    return payload;
  } catch (err: any) {
    // axios / axios-pro 取消：isCancel、__CANCEL__、message=Aborted
    if (
      xAxios.isCancel(err) ||
      err?.__CANCEL__ ||
      err?.name === "AbortError" ||
      err?.message === "Aborted"
    ) {
      throw new DOMException("Aborted", "AbortError");
    }
    const msg =
      err?.response?.data?.msg ||
      err?.response?.data?.message ||
      err?.data?.msg ||
      err?.data?.message ||
      err?.message ||
      "生成失败，请稍后重试";
    throw new Error(msg === "Network Error" ? "生成失败，请稍后重试" : msg);
  }
}

// ===== 接龙展示卡片 / 列表 =====

/** 接龙状态：0未开始 1接龙中 2已结束 -1已取消 */
export type JieLongStatus = 0 | 1 | 2 | -1;

export const JIELONG_STATUS = {
  NOT_STARTED: 0 as JieLongStatus,
  ONGOING: 1 as JieLongStatus,
  ENDED: 2 as JieLongStatus,
  CANCELLED: -1 as JieLongStatus,
};

/** 新建页编辑模式：create 新建 / full 未开始全量编辑 / limited 进行中可改库存限购、延长时间、标题、介绍 */
export type JielongEditMode = "create" | "full" | "limited";

/** 已结束 / 已取消视为同一终态 */
export function isTerminalJieLongStatus (status: JieLongStatus): boolean {
  return status === JIELONG_STATUS.ENDED || status === JIELONG_STATUS.CANCELLED;
}

/** 兼容 iOS：Safari 无法解析 "2026-05-17 20:00:00" 这类空格分隔时间 */
export function parseJielongTime (t?: string | null): number {
  if (!t) return 0;
  const ms = new Date(t).getTime();
  if (!isNaN(ms)) return ms;
  const fallback = new Date(String(t).replace(/-/g, "/")).getTime();
  return isNaN(fallback) ? 0 : fallback;
}

function toJielongTimeMs (value: string | Date | null | undefined): number {
  if (!value) return 0;
  if (value instanceof Date) {
    const ms = value.getTime();
    return isNaN(ms) ? 0 : ms;
  }
  return parseJielongTime(value);
}

/** 接龙中倒计时结束点：有有效延长时间则优先使用 */
export function pickJielongEndCountdownTime (
  status: JieLongStatus,
  endTime?: string,
  extendTime?: string
): string {
  if (status === JIELONG_STATUS.ONGOING && extendTime && parseJielongTime(extendTime)) {
    return extendTime;
  }
  return endTime || "";
}

export const JIELONG_EXTEND_TIME_ERROR = {
  AFTER_END: "接龙延长时间需晚于原结束时间",
  AFTER_NOW: "接龙延长时间需晚于当前时间",
} as const;

/** 延长时间非必填；有值时须晚于原结束时间且晚于当前时间 */
export function getJielongExtendTimeError (
  extendTime: string | Date | null | undefined,
  endTime: string | Date | null | undefined,
  nowMs: number = Date.now()
): string {
  if (extendTime == null || extendTime === "") return "";
  const extendMs = toJielongTimeMs(extendTime);
  if (!extendMs) return "";
  const endMs = toJielongTimeMs(endTime);
  if (endMs && extendMs <= endMs) {
    return JIELONG_EXTEND_TIME_ERROR.AFTER_END;
  }
  if (extendMs <= nowMs) {
    return JIELONG_EXTEND_TIME_ERROR.AFTER_NOW;
  }
  return "";
}

/** 列表接口返回的接龙商品简要信息 */
export interface JieLongListGoodsItem {
  minPrice?: number; // 价格下限
  maxPrice?: number; // 价格上限（缺省或等于下限则只显示单价）
  ingredientImageUrl?: string; // 商品图片
}

export interface JieLongCardInfo {
  joinActivityId: string; // 接龙活动编号
  title: string; // 接龙活动标题
  isStorePickup: number; // 是否门店自提(1.是, 0.否)
  isHomeDelivery: number; // 是否送货上门(1.是, 0.否)
  startTime: string; // 接龙开始时间
  endTime: string; // 接龙结束时间
  extendTime?: string; // 接龙延长时间（接龙中可选）
  arrivalTime?: string; // 到货时间
  minOrderAmount: number | null; // 起购金额，未填写为 null
  minOrderQuantity: number | null; // 起购数量，未填写为 null
  storeId: string; // 店铺编号
  status: JieLongStatus; // 接龙状态
  orderCount: number; // 订单数
  actualIncome: number; // 实际收入
  refundAmount: number; // 退款金额
  joinUserCount: number; // 接龙下单人数
  cancelUserCount: number; // 取消接龙人数
  viewCount: number; // 查看接龙人数
  isDraft: number; // 是否草稿(1.是, 0.否)
  /** 是否隐藏：0显示 1隐藏（已结束/已取消可改，后端字段名待确认） */
  isHidden?: number;
  createUser?: string;
  createTime?: string;
  updateUser?: string;
  updateTime?: string;
  // 列表展示用：价格取首个商品，图片取各商品 ingredientImageUrl
  goodsList?: JieLongListGoodsItem[];
}

export interface GetJielongListParameter {
  storeId: string;
  pageIndex: number;
  pageSize: number;
  source: number; // 来源：0.小程序 1.app 2.pc
  status?: JieLongStatus; // 状态：0未开始 1接龙中 2已结束 -1已取消
  startTime?: string; // 开始时间
  endTime?: string; // 结束时间
  title?: string; // 标题
}

// 5.3.8JM008-查询接龙列表 getJoinActivityList
export function getJielongList (
  parameter: GetJielongListParameter
): Promise<Ajax.Response<Ajax.ListData<JieLongCardInfo>>> {
  return axiosAPIUrl<Ajax.Response<Ajax.ListData<JieLongCardInfo>>>({
    url: "/cykjStore/CCX/joinManage/getJoinActivityList",
    method: "get",
    params: parameter,
    // 列表页自带 van-list / pull-refresh loading，关闭全局加载层避免叠两个「加载中」
    extraParams: { showLoading: false },
  })
  // void parameter;
  // const now = Date.now();
  // const mockImages = [
  //   "https://img.yzcdn.cn/vant/cat.jpeg",
  //   "https://img.yzcdn.cn/vant/apple-1.jpg",
  //   "https://img.yzcdn.cn/vant/apple-2.jpg",
  //   "https://img.yzcdn.cn/vant/apple-3.jpg",
  // ];
  // const mock: JieLongCardInfo[] = [
  //   {
  //     joinActivityId: "1",
  //     title: "[6月20日提货] 常常鲜大促接龙",
  //     isStorePickup: 1,
  //     isHomeDelivery: 0,
  //     startTime: "2026-05-18 08:00:00",
  //     endTime: new Date(now + 60 * 60 * 1000 + 30 * 1000).toISOString(),
  //     minOrderAmount: 0,
  //     minOrderQuantity: 1,
  //     storeId: "store001",
  //     status: 2,
  //     orderCount: 8,
  //     joinUserCount: 8,
  //     cancelUserCount: 1,
  //     viewCount: 50,
  //     isDraft: 0,
  //     createTime: "2026-05-18 08:00:00",
  //     minPrice: 3,
  //     maxPrice: 29.9,
  //     images: mockImages,
  //     actualIncome: 120,
  //     refundAmount: 12,
  //     arrivalTime: "2026-07-10 10:00:00",
  //   },
  //   {
  //     joinActivityId: "2",
  //     title: "[6月18日提货] 周末水果特惠接龙",
  //     isStorePickup: 1,
  //     isHomeDelivery: 1,
  //     startTime: "2026-05-16 09:00:00",
  //     endTime: "2026-05-17 20:00:00",
  //     minOrderAmount: 0,
  //     minOrderQuantity: 1,
  //     storeId: "store001",
  //     status: 3,
  //     orderCount: 15,
  //     joinUserCount: 15,
  //     cancelUserCount: 0,
  //     viewCount: 88,
  //     isDraft: 0,
  //     minPrice: 9.9,
  //     images: mockImages.slice(0, 3),
  //     actualIncome: 260,
  //     refundAmount: 0,
  //     arrivalTime: "2026-07-10 10:00:00",
  //   },
  //   {
  //     joinActivityId: "3",
  //     title: "[已取消] 端午礼盒接龙",
  //     isStorePickup: 0,
  //     isHomeDelivery: 1,
  //     startTime: "2026-05-10 10:00:00",
  //     endTime: "2026-05-11 20:00:00",
  //     minOrderAmount: 0,
  //     minOrderQuantity: 1,
  //     storeId: "store001",
  //     status: 4,
  //     orderCount: 0,
  //     joinUserCount: 3,
  //     cancelUserCount: 3,
  //     viewCount: 20,
  //     isDraft: 0,
  //     minPrice: 59,
  //     maxPrice: 199,
  //     images: mockImages.slice(1, 4),
  //     actualIncome: 0,
  //     refundAmount: 177,
  //     arrivalTime: "2026-07-10 10:00:00",
  //   },
  //   {
  //     joinActivityId: "4",
  //     title: "[6月25日提货] 会员日蔬菜接龙",
  //     isStorePickup: 1,
  //     isHomeDelivery: 0,
  //     startTime: new Date(now + 2 * 24 * 60 * 60 * 1000).toISOString(),
  //     endTime: new Date(now + 5 * 24 * 60 * 60 * 1000).toISOString(),
  //     minOrderAmount: 0,
  //     minOrderQuantity: 1,
  //     storeId: "store001",
  //     status: 1,
  //     orderCount: 0,
  //     joinUserCount: 0,
  //     cancelUserCount: 0,
  //     viewCount: 6,
  //     isDraft: 0,
  //     minPrice: 5,
  //     maxPrice: 18.8,
  //     images: mockImages.slice(0, 2),
  //     actualIncome: 0,
  //     refundAmount: 0,
  //     arrivalTime: "2026-07-10 10:00:00",
  //   },
  // ];
  // return new Promise(resolve => setTimeout(() => resolve({
  //   code: "0",
  //   data: {
  //     pageIndex: 1,
  //     pageSize: 10,
  //     records: mock,
  //     statistics: null,
  //     totalPages: 1,
  //     totalRecords: mock.length,
  //   },
  //   error: null,
  //   msg: "success",
  //   reqid: null,
  //   timestamp: String(Date.now()),
  // }), 0));
}

// JM007-批量结束 batchEnd
export function endJielong (data: { joinActivityList: any[], storeId: string }): Promise<any> {
  return axiosAPIUrl<Ajax.Response<any>>({
    url: "/cykjStore/CCX/joinManage/batchEnd",
    method: "post",
    data,
  })
}

// JM006-批量取消 batchCancel
export function cancelJielong (data: { joinActivityList: any[], storeId: string }): Promise<any> {
  return axiosAPIUrl<Ajax.Response<any>>({
    url: "/cykjStore/CCX/joinManage/batchCancel",
    method: "post",
    data,
  })
}

/** 接龙商品标签 */
export interface JielongGoodsTag {
  sortNo: number;
  tagName: string;
}

// ===== 接龙商品 / 设置 / 预览 =====

/** join_activity_goods 对应前端商品项 */
export interface JielongGoodsItem {
  _key: string;
  joinActivityGoodsId?: string;
  goodsId: string;
  saleTitle: string;
  tagList: JielongGoodsTag[];
  joinSellUnit: number;
  sellUnitWay: string;
  joinPrice: number | null;
  stockQty: number | null; // 库存数量
  soldQty: number; // 已接龙数量
  showStock: number; // 小程序是否显示库存：1.是 0.否
  minBuyQty: number | null; // 最小购买数量
  maxBuyQty: number | null; // 最大购买数量
  originalPrice: number | null; // 划线价
  sortNo: number;
  goodsImage?: string;
  /** 商品图片列表（选品页 storeImageList 映射而来） */
  imgList?: Array<{ imageUrl?: string; [key: string]: any }>;
  /** 发布时间 */
  createTime?: string
  /** 到货时间 */
  arrivalTime?: string
  /** 接龙活动id */
  joinActivityId?: string
  isJoinGoods?: number; // 用于复制接龙判断商品是否失效或者不是接龙商品：1.是 0.否
}

/** 接龙商品 → AI 五字段；单次最多 50 个（常小二侧截断上限） */
export function mapGoodsToAiProducts (goods: JielongGoodsItem[]): AiGenerateProduct[] {
  return (goods || []).slice(0, 50).map((g) => {
    const tags = [...(g.tagList || [])]
      .sort((a, b) => (a.sortNo || 0) - (b.sortNo || 0))
      .map((t) => t.tagName)
      .filter(Boolean)
      .slice(0, 2);
    return {
      saleTitle: String(g.saleTitle || "").trim(),
      tags: tags.length ? tags : null,
      specification: `${g.joinSellUnit || ""}${g.sellUnitWay || ""}`.trim(),
      price: Number(g.joinPrice),
      originalPrice:
        g.originalPrice == null || Number.isNaN(Number(g.originalPrice))
          ? null
          : Number(g.originalPrice),
    };
  });
}

/** 从选品/商品接口数据中解析图片路径 */
export function pickGoodsImage (source: Record<string, any> | null | undefined): string {
  if (!source) return "";
  return (
    source.ingredientImageUrl ||
    source.goodsImage ||
    source.goodsImageUrl ||
    source.imageNameUrl ||
    source.goodsMainImage ||
    source.imageUrl ||
    ""
  );
}

/** 接龙设置表单（新建页 / 预览共享） */
export interface JielongSettingForm {
  isStorePickup: number;
  isHomeDelivery: number;
  startTime: string;
  endTime: string;
  /** 接龙延长时间，仅接龙中可填，未填写传空字符串 */
  extendTime?: string;
  arrivalTime: string;
  /** 未填写时传后端 null */
  minOrderAmount: number | null;
  /** 未填写时传后端 null */
  minOrderQuantity: number | null;
}

/** 接龙浏览用户（详情态头像展示） */
export interface JielongBrowseUser {
  avatarUrl?: string;
  browseId?: string;
  userName?: string;
}

/** 预览活动信息 = join_activity，去掉列表商品字段 */
export type JielongPreviewActivity = Omit<
  JieLongCardInfo,
  "goodsList" | "joinActivityId" | "storeId"
> & {
  joinActivityId?: string;
  storeId?: string;
  /** 最近查看用户（详情态展示头像，最多 3 个） */
  browseList?: JielongBrowseUser[];
};

/** 接龙记录下单品项 */
export interface JielongRecordGoods {
  goodsName: string;
  saleAmount: number;
}

/** 接龙记录（按用户聚合） */
export interface JielongRecordItem {
  orderId?: string;
  joinActivityId?: string;
  name: string;
  headImg?: string;
  createTime: string;
  joinList: JielongRecordGoods[];
  sort: number | string;
}

/** 前端预览/编辑用：活动信息与列表拆开，便于组件传参 */
export interface JielongPreviewPayload {
  activity: JielongPreviewActivity;
  detailList: JielongIntroCardDetail[];
  goodsList: JielongGoodsItem[];
  /** 预览页提交时区分：create（含草稿）走创建，full/limited 走更新 */
  editMode?: JielongEditMode;
}

/**
 * 详情接口原始结构：活动字段与 detailList、goodsList 同级（无 activity 嵌套）
 */
export type JielongDetailApiData = JielongPreviewActivity & {
  detailList?: Array<JielongIntroCardDetail | JielongIntroCardDetailApi>;
  goodsList?: JielongGoodsItem[];
};

/** 将详情接口扁平数据整理为前端预览结构 */
export function normalizeJielongDetail (raw: JielongDetailApiData | null | undefined): JielongPreviewPayload {
  const data = (raw || {}) as JielongDetailApiData;
  const { detailList: rawDetails, goodsList: rawGoods, ...activity } = data;
  return {
    activity: activity as JielongPreviewActivity,
    detailList: parseDetailListFromApi(rawDetails),
    goodsList: (rawGoods || []).map((g: any, idx: number) => ({
      ...g,
      _key: g._key || `detail-${g.goodsId || idx}`,
      goodsImage: pickGoodsImage(g) || g.goodsImage || "",
      tagList: Array.isArray(g.tagList) ? g.tagList : [],
    })),
  };
}

export const JIELONG_PREVIEW_STORAGE_KEY = "jielongPreviewPayload";

export type JielongPreviewTab = "intro" | "goods" | "records";

const JIELONG_STATUS_LABEL: Record<JieLongStatus, { list: string; preview: string }> = {
  0: { list: "未开始", preview: "接龙尚未发起" },
  1: { list: "接龙中", preview: "接龙中" },
  2: { list: "已结束", preview: "已结束" },
  [-1]: { list: "已取消", preview: "已取消" },
};

/** 详情态：按发起时间生成「n天前发起」 */
export function formatJieLongLaunchedLabel (launchedAt?: string): string {
  if (!launchedAt) return "已发起";
  const target = new Date(launchedAt).getTime();
  const ms = isNaN(target) ? new Date(launchedAt.replace(/-/g, "/")).getTime() : target;
  if (isNaN(ms)) return "已发起";
  const days = Math.max(0, Math.floor((Date.now() - ms) / (24 * 60 * 60 * 1000)));
  if (days === 0) return "今天发起";
  return `${days}天前发起`;
}

export function getJieLongStatusLabel (
  status: JieLongStatus,
  scene: "list" | "preview" = "list"
): string {
  return JIELONG_STATUS_LABEL[status][scene];
}

export function getJielongRecords (params: {
  storeId: string
  joinActivityId: string
  pageIndex: number
  pageSize: number
}): Promise<Ajax.Response<Ajax.ListData<JielongRecordItem>>> {
  return axiosAPIUrl<Ajax.Response<Ajax.ListData<JielongRecordItem>>>({
    url: "/cykjStore/CCX/joinManage/getJoinRecords",
    method: "get",
    params,
  })
}

// 5.3.9 JM013-获取接龙详情 getJoinActivityDtl
export function getJielongDetail (params: {
  storeId: string
  joinActivityId: string
}): Promise<Ajax.Response<JielongDetailApiData>> {
  return axiosAPIUrl<Ajax.Response<JielongDetailApiData>>({
    url: "/cykjStore/CCX/joinManage/getJoinActivityDtl",
    method: "get",
    params,
  })
}

/** 创建接龙（立即发起） */
export type CreateJielongPayload = JielongSettingForm & {
  storeId: string;
  title: string;
  goodsList: JielongGoodsItem[];
  detailList: JielongIntroCardDetailApi[];
  createType: number; // 创建类型：1.立即发起 2.保存草稿
};

/** 更新接龙：updateType=1 未开始全量修改；updateType=2 接龙中改库存/限购/延长时间/标题/介绍；updateType=3 已结束/已取消改显示隐藏 */
export type UpdateJielongPayload = Partial<JielongSettingForm> & {
  storeId: string;
  joinActivityId: string;
  updateType: number; // 修改类型：1.未开始修改 2.接龙中修改 3.终态显示隐藏
  title?: string;
  /** 是否隐藏：0显示 1隐藏（updateType=3 时使用，后端字段名待确认） */
  goodsList?: JielongGoodsItem[] | UpdateJielongGoodsLimitsPayload["goodsList"];
  detailList?: JielongIntroCardDetailApi[];
};

/** 进行中仅更新商品库存 / 限购 */
export interface UpdateJielongGoodsLimitsPayload {
  joinActivityId: string;
  goodsList: Array<{
    joinActivityGoodsId?: string;
    goodsId: string;
    stockQty: number | null;
    showStock: number;
    minBuyQty: number | null;
    maxBuyQty: number | null;
  }>;
}

// 5.3.4 JM004-发起接龙 createJoin
export function createJielong (payload: CreateJielongPayload): Promise<Ajax.Response<{ joinActivityId: string }>> {
  return axiosAPIUrl<Ajax.Response<{ joinActivityId: string }>>({
    url: "/cykjStore/CCX/joinManage/createJoin",
    method: "post",
    data: payload,
  })
}

// JM019-店铺是否存在接龙草稿 isJoinActivityDraft
export function getJielongDraft (params: Parameter = {}) {
  return axiosAPIUrl<any>({
    url: "/cykjStore/CCX/joinManage/isJoinActivityDraft",
    method: "get",
    params,
    // 草稿状态为旁路查询，不打断列表页 loading 体验
    extraParams: { showLoading: false },
  })
}

// 2.5 全量更新接龙 updateJielong
export function updateJielong (payload: UpdateJielongPayload): Promise<Ajax.Response<any>> {
  return axiosAPIUrl<Ajax.Response<any>>({
    url: "/cykjStore/CCX/joinManage/updateJoin",
    method: "post",
    data: payload,
  })
}

// 2.6 进行中更新库存/限购 updateJielongGoodsLimits
export function updateJielongGoodsLimits (
  payload: UpdateJielongGoodsLimitsPayload
): Promise<void> {
  // TODO 对接后端：
  // return axiosAPIUrl<Ajax.Response<void>>({
  //   url: "/cykjStore/CCX/joinManage/updateJielongGoodsLimits",
  //   method: "post",
  //   data: payload,
  // })
  void payload;
  return Promise.resolve();
}

// 12.3JM003-JM003-接龙活动数据导出Excel joinExport
export function joinExport (params: Parameter = {}) {
    // return exportAxios<Ajax.Response>({
    //     url: '/ccxApolet/joinManage/joinExport',
    //     method: 'post',
    //     data: { ...params, ...assignParams() },
    //     responseType: 'arraybuffer'
    // })
    return exportAxios<Ajax.Response<any>>({
        url: '/ccxApolet/joinManage/joinExport',
        method: 'post',
        data: ({ ...params, ...assignParams()})
      })
}

// JM001-新增商品标签 createGoodsTag
export function createGoodsTag (params: { storeId: string; tagName: string } & Parameter) {
  return axiosAPIUrl<Ajax.Response>({
    url: "/cykjStore/CCX/joinManage/createGoodsTag",
    method: "post",
    data: { ...params, ...assignParams() },
  });
}

// JM002-删除商品标签 deleteGoodsTag
export function deleteGoodsTag (params: { storeId: string; tagId: string } & Parameter) {
  return axiosAPIUrl<Ajax.Response>({
    url: "/cykjStore/CCX/joinManage/deleteGoodsTag",
    method: "post",
    data: { ...params, ...assignParams() },
  });
}

// JM003-获取常用标签列表
export function getTagConfigList (params: {
  storeId: string;
  pageIndex: number;
  pageSize: number;
} & Parameter) {
  return axiosAPIUrl<Ajax.Response<Ajax.ListData<GoodsTagConfigItem>>>({
    url: "/cykjStore/CCX/joinManage/getTagConfigList",
    method: "get",
    params: { ...params, ...assignParams() },
  });
}

/** 拉取并映射常用标签列表（同参并发共用一个请求，避免 cancelRepeatRequest 互相取消） */
let fetchCommonTagsPromise: Promise<CommonTag[]> | null = null;
let fetchCommonTagsStoreId = "";

export async function fetchCommonTags (storeId: string): Promise<CommonTag[]> {
  if (fetchCommonTagsPromise && fetchCommonTagsStoreId === storeId) {
    return fetchCommonTagsPromise;
  }
  fetchCommonTagsStoreId = storeId;
  fetchCommonTagsPromise = getTagConfigList({
    storeId,
    pageIndex: 1,
    pageSize: 100,
  })
    .then(res => {
      const records = (res.data && res.data.records) || [];
      return records.map(mapTagConfigToCommonTag);
    })
    .finally(() => {
      fetchCommonTagsPromise = null;
      fetchCommonTagsStoreId = "";
    });
  return fetchCommonTagsPromise;
}

// 5.3.11 JM015-获取订单汇总数据 getJoinOrderData
export function getJoinOrderData (params: any) {
  return axiosAPIUrl<Ajax.Response<Ajax.ListData<GoodsTagConfigItem>>>({
    url: "/cykjStore/CCX/joinManage/getJoinOrderData",
    method: "get",
    params: { ...params, ...assignParams() },
  });
}

// 5.3.10 JM010-直接核销 directRedemption
export function directRedemption (params: any) {
  return axiosAPIUrl<Ajax.Response>({
    url: "/cykjStore/CCX/joinManage/directRedemption",
    method: "post",
    data: { ...params, ...assignParams() },
  });
}

// 5.3.7 JM011-批量核销 batchDirectRedemption
export function batchDirectRedemption (params: any) {
  return axiosAPIUrl<Ajax.Response>({
    url: "/cykjStore/CCX/joinManage/batchDirectRedemption",
    method: "post",
    data: { ...params, ...assignParams() },
  });
}

// 5.3.8 JM012-批量发货 batchShip
export function batchShip (params: any) {
  return axiosAPIUrl<Ajax.Response>({
    url: "/cykjStore/CCX/joinManage/batchShip",
    method: "post",
    data: { ...params, ...assignParams() },
  });
}

// 5.3.13 JM017-提货 pickUpGoods
export function pickUpGoods (params: any) {
  return axiosAPIUrl<Ajax.Response>({
    url: "/cykjStore/CCX/joinManage/pickUpGoods",
    method: "post",
    data: { ...params, ...assignParams() },
  });
}

// 5.3.12 JM016-获取商品统计数据 getJoinGoodsData
export function getJoinGoodsData (params: any) {
  return axiosAPIUrl<Ajax.Response>({
    url: "/cykjStore/CCX/joinManage/getJoinGoodsData",
    method: "get",
    params: { ...params },
  });
}

// 5.3.14 JM018-获取订单列表 getJoinGoodsList
export function getJoinGoodsList (params: any) {
  return axiosAPIUrl<Ajax.Response>({
    url: "/cykjStore/CCX/joinManage/getJoinGoodsList",
    method: "post",
    data: { ...params },
  });
}

// 1.1 JM020-保存商家备注 saveStoreRemark
export function saveStoreRemark (params: any) {
  return axiosAPIUrl<Ajax.Response>({
    url: "/cykjStore/CCX/joinManage/saveStoreRemark",
    method: "post",
    data: { ...params },
  });
}

// JM021-修改接龙是否隐藏  updateJoinExtendTime
export function updateJoinExtendTime (params: {
  joinActivityId: string;
  extendTime?: string;
  isHidden?: number;
}) {
  return axiosAPIUrl<Ajax.Response>({
    url: "/cykjStore/CCX/joinManage/updateJoinExtendTime",
    method: "post",
    data: { ...params },
  });
}